import { IAttribute, ITag } from './Franchise'
import { IEmailFields } from './EmailTypes'
import { IChecklistConditionalRule } from './ConditionalRule'
import { AssigneeType, Omit } from '../types'
import { IPerson } from './Person'
import { IFormula, IFormulaQuestion } from './Formula'
import { ICanvasProps } from '../components/form_controls/CanvasControl'

export interface IChecklistTemplates {
  templates: IChecklistTemplateMeta[]
}

export interface IChecklistTemplateMeta {
  id: number
  templateId?: number
  name: string
  version: string
  checklistEntitySetting?: string
}
export interface IChecklistTemplateList {
  templates: IChecklistTemplate[]
}

export interface IChecklistTemplate {
  id: number
  name: string
}

// We use 0 to indicate no id because null is not an indexed value in indexedDB
export interface IStoredChecklist {
  localId?: number
  versionId: number
  checklistId: number // 0 = not submitted, >0 = submitted checklistId
  franchiseeId: number
  siteId: number
  date: string // YYYY-MM-DD
  checklist: IChecklist
}

export interface IStoredChecklistMeta {
  // Header information returned by e.g. getDraftsList()
  localId: number
  templateId: number
  checklistId: number
  franchiseeId: number
  siteId: number
  date: string
  checklistName: string
}

export interface IChecklist {
  id: number
  template: boolean
  templateId?: number //OP_TEMPLATE_CHECKLIST object_id
  templateName?: string
  name: string
  version: string | null
  date: string | null
  useTabs: boolean
  attributes?: IAttribute[]
  favouredChecklist?: boolean
  status?: 'draft'
  tags?: ITag[]
  site: {
    id: number | null
    name: string
  } | null
  franchisee?: {
    id: number | null
    name: string
  } | null
  gallery: IGallery
  groups: IChecklistGroup[]
  locked: boolean
  useAttributes: boolean
  toEmail: boolean
  editEmail?: boolean
  hasEvent: boolean
  newPhotoComments?: IPhotoComment[] // new photo comments need to be submitted to server
  hideGroupPhotos: boolean // show/hide the group gallery
  hideQuestionNumber?: boolean

  people?: IPerson[]
  hardAssociation?: boolean
  deletedPhotos?: number[]
  usePeople?: boolean // if the template has linked person types
}

export interface IChecklistGroup {
  id: number
  label: string
  fields: IChecklistField[]
  photos: IPhoto[] | null
  checklistGroupId?: number | null
  toComplete: number
}

export interface IChecklistField {
  id: number
  label: string
  type: string
  trackField: boolean
  value?: any
  values?: any[]
  signatureData?: string
  comment: string
  mandatory: boolean
  mandatoryComment: boolean
  mandatoryPhoto: boolean
  commentOption: ChecklistFieldAccessoryOptions
  photoOption: ChecklistFieldAccessoryOptions
  attachmentOption: ChecklistFieldAccessoryOptions
  header?: string
  description?: string
  helpLinks?: IHelpLink[]
  helpImages?: IPhotoHelp[] // also has contentType, fileName, etc
  photos?: IPhoto[] | null
  dataGrid?: IDataGridFieldProps
  importanceAnswerColorRules?: IChecklistColorRule[]
  showQuestion: boolean
  franchiseeAttributes: IAttribute[]
  siteAttributes: IAttribute[]
  options?: Array<{ label: string; value: string }>
  checklistTemplateFieldPresetTicketJsons?: IChecklistTicketJson[]
  dateFormat?: string
  dateTimeFormat?: string
  dateTimeIncludeOffset?: boolean
  hidden?: boolean
  conditions?: number[]
  conditionalRule?: IChecklistConditionalRule | null
  key?: string
  formula?: IFormula
  formulaQuestions?: IFormulaQuestion[] // for input question
  photoData?: ICanvasProps
}

export type IGallery = Omit<IChecklistField, 'id'> & {
  id: string | null
}
export interface IFieldRenderProps {
  hidden?: boolean
  questionNumber?: string
  // added it for refreshing quesiton number when using conditional question
  questionNeedRefresh?: boolean
}

export interface IAddRenderPropsParams {
  assigneeType: AssigneeType
  assigneeAttributes: IAttribute[]
  template: IChecklist
}

export interface IGroupRenderProps {
  fields: IFieldRenderProps[]
  incompleteFields: boolean
  showTab: boolean
}

export interface IChecklistRenderProps {
  groups: IGroupRenderProps[]
}

export interface IChecklistColorRule {
  importanceAnswerType: string
  color: string
  stringValue: string
  lowValue?: string
  highValue?: string
}

export interface IChecklistTicketJson {
  answer: 'NOT_TICKED' | 'TICKED' | string
  useTicket: boolean
  categoryId: number // -1 for none
  tags: ITag[]
  priority: 'Low' | 'Medium' | 'High'
  // when prepopulatingContent is false, user has to input note. we need make sure note is not null
  // this only apply to popup tickets but not ad-hoc tickets on checklist page
  prepopulatingContent: boolean
  prepopulatingSubject?: boolean
}

export interface IPhoto {
  // Canonical image URLs - either https://... or storedimg://...
  url: string
  id?: number | null // added photo id so we can link new comments to photo
  thumbnailUrl: string
  // Blob URLs used for rendering local images.
  // These should be freed when the checklist is closed.
  localObjectUrl?: string
  localThumbnaiObjectlUrl?: string
  comment: string
  comments?: IPhotoComment[]
  fileName: string
  fileSize?: number
  contentType?: string
  photoTakenDate?: string | null // YYYY:MM:DD HH:mm:ss
  uuid?: string | null //this attribute indicates the attachment is temporary
  attachmentType?: 'photo' | 'attachment'
  source?: 'gallery' | 'camera'
}

export interface IPhotoComment {
  name: string
  id: number | null
  comment?: string
  createdDateTime?: string // dd/mm/YYYY HH:mm:ss a
  photoId: number | null
  action?: 'add' | 'update' | 'delete'
}

export interface IAttachment {
  id: number | null
  url: string | null
  localObjectUrl?: string | null
  fileName: string
  fileSize?: number
  contentType?: string
  comment?: string
  fileSizeExceeded?: boolean
  file?: Blob | null

  //checklist specific fields
  fieldId?: number
  rowIdx?: number
  colIdx?: number

  //when dealing with temporary attachments
  status: 'Error' | 'Saving' | 'Saved' | 'Canceled' | null
  uuid: string | null
  entityId?: number
  forEntity?: 'checklist' | 'incident' | 'hazard'
}

export interface ISubmitChecklistResponse {
  success: boolean
  data: any
}

export interface IDataGridFieldCellProps {
  offset: number
  label: string
  field: IChecklistField | null
  id?: number | null
}

export interface IDataGridFieldFormulaProps {
  offset: number
  formula: string
  id: number | null
}

export interface IDataGridFieldProps {
  rows: number
  columns: number
  showRowLabels: boolean
  rowLabels: IDataGridFieldCellProps[]
  columnLabels: IDataGridFieldCellProps[]
  formulas: IDataGridFieldFormulaProps[]
  fittingSinglePage: boolean
  values: IChecklistField[][] | null
  id: number
  expandFlags?: boolean[] // expand flags for rows in data grid list
}

export interface IChecklistEmailResponse {
  score: number
  unansweredQuestions: number
  message: IEmailFields
}

export interface IFavouriteChecklist {
  creator: string
  date: string
  franchiseeId: number
  franchiseeName: string
  id: number
  locked: boolean
  name: string
  score: number
  siteName: string
  ticketNo: number
  unansweredQuestions: number
  version: string
}

export interface IChecklistSearchParams {
  siteId?: number
  templateId?: number
  name?: string
  fromDate?: Date | string
  toDate?: Date | string
  selectedPeople?: number[]
}

export interface ICompletedChecklist {
  creator: string
  date: string
  id: number
  locked: boolean
  name: string
  score: number
  siteId?: number
  siteName?: string
  franchiseeId?: number
  franchiseeName?: string
  ticketNo: number | null
  unansweredQuestions: number
  version: string
}

export interface IHelpLink {
  link: string
  type: string
  id: number
}

export interface IPhotoHelp {
  // Canonical image URLs - either https://... or storedimg://...
  url: string
  id?: number | null // added photo id so we can link new comments to photo
  // Blob URLs used for rendering local images.
  // These should be freed when the checklist is closed.
  comment: string | null
  fileName: string
  fileSize?: number
  contentType?: string
  photoTakenDate?: string | null // YYYY:MM:DD HH:mm:ss
}

export type ChecklistFieldAccessoryOptions =
  | 'optional'
  | 'mandatory'
  | 'not_allowed'

export interface IQuestionIconOptions {
  commentIcon: boolean
  commentIconClass: string
  commentIconShow: boolean
  photoIcon: boolean
  photoIconClass: string
  photoIconShow: boolean
  attachmentIcon: boolean
  attachmentIconClass: string
  attachmentIconShow: boolean
  helpImageIcon: boolean
  helpImageIconShow: boolean
  helpTextIcon: boolean
  helpTextIconShow: boolean
  menuIconClass: string
}

export enum PhotoQuestionAnswer {
  Completed = 'Completed'
  // NotCompleted = ''
}

export type ChecklistPhotoList = {
  fieldId: string
  groupId: string
  row?: number
  col?: number
  photo: IPhoto
}[]
